/**
 * VRG-721 — the client-side budget every AUTOMATIC report draws from.
 *
 * Extracted from `errorReporter.ts`, where it was two module-level variables
 * serving render crashes only (VRG-427). Error toasts, `window.onerror` and
 * `unhandledrejection` now report too, and the requirement is that they share
 * the SAME caps — so the budget is one object, created once, consulted by all
 * four paths. Four separate budgets would let a page with a failing endpoint, a
 * noisy handler and a crash send 20 requests where the design allows 5.
 *
 *  - `maxPerSession` (5): hard cap per tab JS context. A full reload resets it,
 *    which is also when a fresh budget is correct (the crash recovery is a reload).
 *  - `minIntervalMs` (5s): the render path's per-signature cooldown, unchanged.
 *  - `once`: the toast/global paths report a signature at most ONCE per tab —
 *    "the first occurrence" — rather than every 5s. A toast repeats because the
 *    user clicked again, and the server already counts repeats on the row it has.
 *
 * Pure and clock-injectable so the caps are unit-tested
 * (`test/support-error-report.guard.spec.ts`).
 */
export interface ReportBudget {
  /** True when this occurrence may be sent; records it as sent. */
  tryConsume: (signature: string, opts?: { once?: boolean; now?: number }) => boolean
  /** How many reports this tab has sent. */
  sent: () => number
}

export const MAX_REPORTS_PER_TAB_SESSION = 5
export const MIN_INTERVAL_MS_PER_SIGNATURE = 5000

export const createReportBudget = (
  maxPerSession: number = MAX_REPORTS_PER_TAB_SESSION,
  minIntervalMs: number = MIN_INTERVAL_MS_PER_SIGNATURE,
): ReportBudget => {
  let sent = 0
  const lastSentAt = new Map<string, number>()
  return {
    tryConsume: (signature, opts = {}) => {
      const now = opts.now ?? Date.now()
      if (sent >= maxPerSession) return false
      const last = lastSentAt.get(signature)
      if (last !== undefined) {
        if (opts.once) return false
        if (now - last < minIntervalMs) return false
      }
      lastSentAt.set(signature, now)
      sent += 1
      return true
    },
    sent: () => sent,
  }
}
